/**
 * `lib/anchoring` — what is LEFT of the DOM anchoring glue.
 *
 * This module used to be the read surface's anchor engine: it stamped the
 * server's `rendered_html` with `.docz-block[data-block-id]`, turned a
 * `window.getSelection()` into a durable W3C selector, re-anchored every stored
 * comment by walking text nodes into a `Range`, wrapped that range in
 * `<span class="docz-comment">`, and bloomed it. ~530 lines of it.
 *
 * ALL OF THAT IS DELETED. The canvas is the only document surface (owner
 * directive 2026-07-28, executed 2026-07-30), and a `<canvas>` has no text
 * nodes, no `Range` and no spans — the only coordinate that exists is
 * `(blockOrd, block-relative UTF-8 byte offset)`. The canvas equivalents live in
 * `components/canvas/readCanvasAnchors.ts` (resolve, draft, jump) and
 * `components/canvas/readCanvasHighlights.ts` (paint, hit-test, bloom), and they
 * resolve against the docz IR rather than against markup.
 *
 * WHY THE FILE SURVIVES AT ALL. Two things in it were never about the DOM:
 *
 *  · {@link hashQuote} — the brief's `quote_hash`, a pure string→string hash. It
 *    is the confidence term in the canvas resolver's `exact` predicate and the
 *    id source for the mock adapter's content hashes. Nothing about it assumed a
 *    document surface.
 *  · `AnchorState` — web-docz's `exact | moved | orphan`, re-exported here
 *    because most of the app imports the vocabulary from this path. It is THREE
 *    values, and deliberately not the FIVE on the wire: the server's
 *    `anchor_state` column carries the four docz `RebaseOutcome` states plus the
 *    disjoint `unanchored` (no anchor row exists at all — `db/models.zig:205`),
 *    which no client re-anchor pass can produce.
 */

import type { AnchorState } from "@norkron/web-docz";

export type { AnchorState };

/** A stable, order-independent hash of the exact quote (the brief's `quote_hash`
 *  — exact-match ⇒ confidence 1.0). djb2, hex. */
export function hashQuote(quote: string): string {
  let h = 5381;
  for (let i = 0; i < quote.length; i += 1) {
    h = ((h << 5) + h + quote.charCodeAt(i)) >>> 0;
  }
  return h.toString(16);
}
